// WebRTC типы
// Shared signaling and metrics
// Shared signaling and metrics types used by both client and server

// Minimal ICE candidate shape to avoid DOM dependency on the server
export interface IceCandidateInitLike {
  candidate: string;
  sdpMid?: string | null;
  sdpMLineIndex?: number | null;
  usernameFragment?: string | null;
}

export interface PeerMetrics {
  codecs: {
    video: string | null;
    audio: string | null;
  };
  resolution: {
    width: number;
    height: number;
  };
  network: {
    rtt: number;
    bitrateOut: number;
    bitrateIn: number;
    packetLoss: number;
    connectionType: string;
  };
  media: {
    audioEnabled: boolean;
    videoEnabled: boolean;
  };
  camera: {
    type: string;
    label: string;
    canSwitch: boolean;
    availableCameras: number;
  };
  // quality is a string here to decouple from client-only QualityLevel type
  quality: string;
}

export interface SignalingMessage<T = any> {
  type: string;
  payload: T;
}

export interface JoinPayload {
  roomId: string;
  token: string;
  displayName: string;
}

export interface JoinedPayload {
  peerId: string;
  roomId: string;
  displayName: string;
  isReconnect?: boolean;
}

export interface PeersPayloadItem {
  peerId: string;
  displayName: string;
}

export interface PeersPayload {
  peers: PeersPayloadItem[];
}

export interface PeerJoinedPayload {
  peerId: string;
  displayName: string;
}

export interface PeerReconnectedPayload {
  peerId: string;
  displayName: string;
}

export interface PeerReadyPayload {
  peerId: string;
}

export interface OfferAcceptPayload {
  sdp: string;
  fromPeerId: string;
}

export interface OfferSendPayload {
  sdp: string;
  targetPeerId: string;
}

export interface AnswerAcceptPayload {
  sdp: string;
  fromPeerId: string;
}

export interface AnswerSendPayload {
  sdp: string;
  targetPeerId: string;
}

export interface IceCandidatePayload {
  candidate: IceCandidateInitLike;
  fromPeerId: string;
  targetPeerId?: string;
}

export interface MetricsPayload {
  to: string;
  metrics: PeerMetrics;
}

export interface PeerLeftPayload {
  peerId: string;
}

export interface PeerMetricsPayload {
  fromPeerId: string;
  metrics: MetricsPayload;
}

export interface ErrorPayload {
  message: string;
  code?: string;
}



export interface QualityProfile {
  width: number;
  height: number;
  frameRate: number;
  maxBitrate: number;
}

export interface QualityProfiles {
  '180p': QualityProfile;
  '360p': QualityProfile;
  '480p': QualityProfile;
  '720p': QualityProfile;
  '1080p': QualityProfile;
}

export type QualityLevel = keyof QualityProfiles;

export interface ConnectionStats {
  rtt: number;
  bitrateOut: number;
  bitrateIn: number;
  packetLoss: number;
}

export interface DeviceCapabilities {
  maxWidth: number;
  maxHeight: number;
  maxFrameRate: number;
  supportedQualities: QualityLevel[];
  isLowEndDevice: boolean;
  isMobile: boolean;
}

export interface CameraInfo {
  deviceId: string;
  label: string;
  type: 'front' | 'back' | 'unknown';
  facingMode?: 'user' | 'environment';
}

export interface CameraState {
  availableCameras: CameraInfo[];
  currentCamera: CameraInfo | null;
  hasFrontCamera: boolean;
  hasBackCamera: boolean;
  canSwitchCameras: boolean;
}

export interface AudioDeviceInfo {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'audiooutput';
}

export interface AudioDeviceState {
  availableInputDevices: AudioDeviceInfo[];
  availableOutputDevices: AudioDeviceInfo[];
  currentInputDevice: AudioDeviceInfo | null;
  currentOutputDevice: AudioDeviceInfo | null;
  hasMultipleInputs: boolean;
  hasMultipleOutputs: boolean;
}

// Signaling and metrics types are re-exported from @shared/signaling

// UI типы
export interface UiNotification {
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
}

export type ConnectionState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';


// ==================== WEBRTC STATS UTILITIES ====================


type StatsByType = {
  'outbound-rtp': RTCOutboundRtpStreamStats;
  'inbound-rtp': RTCInboundRtpStreamStats;
  'candidate-pair': RTCIceCandidatePairStats;
  // 'remote-inbound-rtp': RTCRemoteInboundRtpStreamStatsLike;
  // 'remote-outbound-rtp': RTCRemoteOutboundRtpStreamStatsLike;
};

// 2) Универсальный type guard по строковому типу
export const isStatsType =
    <K extends keyof StatsByType>(t: K) =>
        (r: RTCStats): r is StatsByType[K] =>
            r.type === t;

export enum ConnectionTypeEnum {
    P2P_Direct = 'P2P (direct)',
    P2P_NAT = 'P2P (NAT)',
    P2P_Mixed = 'P2P (mixed)',
    Relay_TURN = 'Relay (TURN)',
    Unknown = 'unknown'
}

export type ConnectionType = ConnectionTypeEnum;

export const isIceCandidate = (r: RTCStats | undefined): r is RTCIceCandidateStatsLike =>
    !!r && (r.type === 'local-candidate' || r.type === 'remote-candidate');